import { useState } from 'react';
import { supabase } from '../supabaseClient';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import QRCode from 'qrcode';
import { v4 as uuidv4 } from 'uuid';
import InputField from '../components/InputField';
import { ChevronDown, Download } from 'lucide-react';

export default function CreateMedicine() {
  const [medicine, setMedicine] = useState({
    brandName: '',
    name: '',
    batch: '',
    mfgDate: '',
    expDate: '',
    manufacturer: '',
    manufacturingAddress: '',
    marketedBy: '',
    licenseNo: '',
    mrp: '',
    storage: '',
  });

  const [errors, setErrors] = useState({});
  const [savedId, setSavedId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;

  // =========================
  // HANDLE INPUT CHANGE
  // =========================
  const handleChange = (e) => {
    const { name, value } = e.target;

    setMedicine((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // =========================
  // VALIDATION
  // =========================
  const validateForm = () => {
    const newErrors = {};

    const TEXT_FIELDS = {
      brandName: 'Brand name',
      name: 'Drug name',
      manufacturer: 'Manufacturer name',
      marketedBy: 'Marketed by',
      licenseNo: 'License number',
      storage: 'Storage instructions',
      manufacturingAddress: 'Manufacturing address',
    };

    // Text field validation
    Object.keys(TEXT_FIELDS).forEach((field) => {
      const label = TEXT_FIELDS[field];
      const value = medicine[field]?.trim();

      if (!value) {
        newErrors[field] = `${label} is required`;
      } else if (value.length < 3) {
        newErrors[field] = `Enter a valid ${label.toLowerCase()}`;
      }
    });

    // Batch validation
    if (!medicine.batch?.trim()) {
      newErrors.batch = 'Batch number is required';
    } else if (medicine.batch.trim().length < 4) {
      newErrors.batch = 'Enter a valid batch number';
    }

    // MRP validation
    if (!medicine.mrp) {
      newErrors.mrp = 'MRP is required';
    } else if (isNaN(medicine.mrp)) {
      newErrors.mrp = 'MRP must be a number';
    } else if (Number(medicine.mrp) <= 0) {
      newErrors.mrp = 'MRP must be greater than 0';
    }

    // Manufacturing date
    if (!medicine.mfgDate) {
      newErrors.mfgDate = 'Manufacturing date is required';
    }

    // Expiry date
    if (!medicine.expDate) {
      newErrors.expDate = 'Expiry date is required';
    }

    // Date validation
    if (medicine.mfgDate && medicine.expDate) {
      const mfg = new Date(medicine.mfgDate);
      const exp = new Date(medicine.expDate);
      const today = new Date();

      if (mfg > today) {
        newErrors.mfgDate = 'Manufacturing date cannot be in the future';
      }

      if (exp <= mfg) {
        newErrors.expDate = 'Expiry date must be after manufacturing date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // =========================
  // SAVE MEDICINE
  // =========================
  const saveMedicine = async () => {
    if (!validateForm()) return;

    setIsSaving(true);

    try {
      const medicineId = uuidv4();
      const qrValue = `${baseUrl}/scan?id=${medicineId}`;

      const slugify = (text) =>
        text
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-');

      const fileName = `qr-${slugify(medicine.name)}-${slugify(
        medicine.batch,
      )}-${medicineId}.svg`;

      const qrSvg = await QRCode.toString(qrValue, {
        type: 'svg',
        width: 1000,
      });

      const { error: uploadError } = await supabase.storage
        .from('qr-codes')
        .upload(fileName, new Blob([qrSvg], { type: 'image/svg+xml' }), {
          upsert: true,
        });

      if (uploadError) throw uploadError;

      const { error: insertError } = await supabase.from('medicines').insert([
        {
          id: medicineId,
          brand_name: medicine.brandName,
          drug_name: medicine.name,
          batch_no: medicine.batch,
          mfg_date: medicine.mfgDate,
          exp_date: medicine.expDate,
          manufacturer: medicine.manufacturer,
          manufacturing_address: medicine.manufacturingAddress,
          marketed_by: medicine.marketedBy,
          license_no: medicine.licenseNo,
          mrp: Number(medicine.mrp),
          storage: medicine.storage,
          qr_url: qrValue,
          qr_file_path: fileName,
        },
      ]);

      if (insertError) throw insertError;

      setSavedId(medicineId);
      setErrors({});
      setShowQRModal(true); // open modal
    } catch (err) {
      setErrors({ general: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  // Download PNG
  const downloadPNG = () => {
    const canvas = document.getElementById('qr-canvas');
    const pngUrl = canvas
      .toDataURL('image/png')
      .replace('image/png', 'image/octet-stream');

    const link = document.createElement('a');
    link.href = pngUrl;
    link.download = 'medicines.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download SVG
  const downloadSVG = () => {
    const svg = document.getElementById('qr-svg');
    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svg);

    const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'medicines.svg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm border p-8">
        <h1 className="text-3xl font-semibold mb-6">Medicine QR Generator</h1>

        {errors.general && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-lg">
            {errors.general}
          </div>
        )}

        <div className="space-y-12">
          {/* ======================= */}
          {/* 1️⃣ MEDICINE INFORMATION */}
          {/* ======================= */}
          <div>
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                Medicine Information
              </h2>
              <p className="text-sm text-gray-500">
                Basic product identification details
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Brand Name *"
                name="brandName"
                placeholder="e.g. Crocin Advance"
                value={medicine.brandName}
                onChange={handleChange}
                error={errors.brandName}
              />

              <InputField
                label="Drug / Generic Name *"
                name="name"
                placeholder="e.g. Paracetamol Tablets IP 650mg"
                value={medicine.name}
                onChange={handleChange}
                error={errors.name}
              />
            </div>
          </div>

          {/* ======================= */}
          {/* 2️⃣ BATCH & VALIDITY */}
          {/* ======================= */}
          <div className="pt-8 border-t">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                Batch & Validity
              </h2>
              <p className="text-sm text-gray-500">
                Manufacturing date, expiry, and pricing details
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Batch Number *"
                name="batch"
                placeholder="e.g. AZC1124"
                value={medicine.batch}
                onChange={handleChange}
                error={errors.batch}
              />

              <InputField
                label="MRP (₹) *"
                type="number"
                name="mrp"
                placeholder="e.g. 45.00"
                value={medicine.mrp}
                onChange={handleChange}
                error={errors.mrp}
                min="0"
                step="0.01"
              />

              <InputField
                label="Manufacturing Date *"
                type="date"
                name="mfgDate"
                value={medicine.mfgDate}
                onChange={handleChange}
                error={errors.mfgDate}
              />

              <InputField
                label="Expiry Date *"
                type="date"
                name="expDate"
                value={medicine.expDate}
                onChange={handleChange}
                error={errors.expDate}
              />
            </div>
          </div>

          {/* ======================= */}
          {/* 3️⃣ MANUFACTURING DETAILS */}
          {/* ======================= */}
          <div className="pt-8 border-t">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                Manufacturing Details
              </h2>
              <p className="text-sm text-gray-500">
                Regulatory and company information
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Manufacturer Name *"
                name="manufacturer"
                placeholder="e.g. ABC Pharmaceuticals Pvt Ltd"
                value={medicine.manufacturer}
                onChange={handleChange}
                error={errors.manufacturer}
              />

              <InputField
                label="Marketed By *"
                name="marketedBy"
                placeholder="e.g. XYZ Healthcare India Pvt Ltd"
                value={medicine.marketedBy}
                onChange={handleChange}
                error={errors.marketedBy}
              />

              <InputField
                label="License Number *"
                name="licenseNo"
                placeholder="e.g. MFG/HP/09/1234"
                value={medicine.licenseNo}
                onChange={handleChange}
                error={errors.licenseNo}
              />

              <InputField
                label="Storage Instructions *"
                name="storage"
                placeholder="e.g. Store below 25°C"
                value={medicine.storage}
                onChange={handleChange}
                error={errors.storage}
              />

              <div className="md:col-span-2">
                <InputField
                  label="Manufacturing Address *"
                  name="manufacturingAddress"
                  placeholder="Full manufacturing address as per drug license"
                  value={medicine.manufacturingAddress}
                  onChange={handleChange}
                  error={errors.manufacturingAddress}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 text-right">
          <button
            onClick={saveMedicine}
            disabled={isSaving}
            className={`
    w-full md:w-auto
    px-6 py-3
    rounded-lg
    font-medium
    text-white
    transition-all duration-200
    ${
      isSaving
        ? 'bg-gray-400 cursor-not-allowed'
        : 'bg-indigo-600 hover:bg-indigo-700 active:scale-95'
    }
  `}
          >
            {isSaving ? 'Saving...' : 'Generate & Save QR'}
          </button>
        </div>

        {showQRModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl p-8 w-[380px] text-center relative">
              {/* Close Button */}
              <button
                onClick={() => setShowQRModal(false)}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>

              <h3 className="text-lg font-semibold mb-4">
                Scan this QR to verify medicine
              </h3>

              <div className="flex justify-center mb-6">
                <QRCodeCanvas
                  id="qr-canvas"
                  value={`${baseUrl}/scan?id=${savedId}`}
                  size={220}
                />

                {/* hidden svg for download */}
                <div className="hidden">
                  <QRCodeSVG
                    id="qr-svg"
                    value={`${baseUrl}/scan?id=${savedId}`}
                    size={220}
                  />
                </div>
              </div>

              <div className="relative flex flex-col items-center mt-6">
                {/* wrapper same width as QR */}
                <div className="w-[220px] relative">
                  {/* Split Button */}
                  <div className="flex w-full">
                    {/* Main Download */}
                    <button
                      onClick={downloadPNG}
                      className="flex items-center justify-center gap-2 w-full py-3 bg-indigo-600 text-white rounded-l-xl hover:bg-indigo-700"
                    >
                      <Download size={16} />
                      Download
                    </button>

                    {/* Arrow */}
                    <button
                      onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                      className="px-3 bg-indigo-600 text-white rounded-r-xl border-l border-indigo-500 hover:bg-indigo-700"
                    >
                      <ChevronDown size={16} />
                    </button>
                  </div>

                  {/* Dropdown */}
                  {showDownloadMenu && (
                    <div className="absolute left-0 top-full mt-2 w-full bg-white border rounded-xl shadow-lg overflow-hidden">
                      <button
                        onClick={() => {
                          downloadPNG();
                          setShowDownloadMenu(false);
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-gray-100"
                      >
                        Download PNG
                      </button>

                      <button
                        onClick={() => {
                          downloadSVG();
                          setShowDownloadMenu(false);
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-gray-100 border-t"
                      >
                        Download SVG
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
